import { z } from 'zod'

import { CurrencyZ } from '@/types/common'
import { CountriesCode } from '@/types/locale'

import { ExpenseFileZ, ExpenseModelZ, ExpenseRequestZ, ExpenseResponseZ } from './expense'

export const DailyAllowanceRateZ = z.object({
	id: z.number(),
	name: z.string(),
	currency: CurrencyZ,
	amount: z.number(),
})
export type DailyAllowanceRate = z.infer<typeof DailyAllowanceRateZ>

const DailyAllowanceItemApiZ = z.object({
	daily_allowance_rate: DailyAllowanceRateZ,
	number_of_days: z.number().positive(),
	amount: z.number(),
})
export type DailyAllowanceItemApi = z.infer<typeof DailyAllowanceItemApiZ>

const DailyAllowanceItemZ = z.object({
	...DailyAllowanceItemApiZ.omit({ number_of_days: true }).shape,
	number_of_days: z.string(),
})
export type DailyAllowanceItem = z.infer<typeof DailyAllowanceItemZ>

const DailyAllowanceRateRequestZ = DailyAllowanceRateZ.pick({
	name: true,
	currency: true,
	amount: true,
}).extend({
	destination_id: z.number(),
})
export type DailyAllowanceRateRequest = z.infer<typeof DailyAllowanceRateRequestZ>

export const DADestinationZ = z.object({
	id: z.number(),
	country_code: z.enum(CountriesCode),
	country: z.string(),
	destination_name: z.string(),
})

const DADestinationWithRatesZ = z.object({
	...DADestinationZ.shape,
	rates: z.array(DailyAllowanceRateZ),
})
export type DADestinationWithRates = z.infer<typeof DADestinationWithRatesZ>

const DADestinationWithRatesRequestZ = DADestinationWithRatesZ.pick({
	country_code: true,
	destination_name: true,
}).extend({
	rates: z.array(DailyAllowanceRateRequestZ),
})
export type DADestinationWithRatesRequest = z.infer<typeof DADestinationWithRatesRequestZ>

export const DailyAllowanceModelZ = z
	.object({
		...ExpenseModelZ.shape,
		amount: z.number(),
		category: z.string().nullish(),
		destination: z.object({ id: z.number(), name: z.string() }),
		endDate: z.date(),
		endTime: z.iso.time(),
		file: ExpenseFileZ.nullish(),
		rates: z.array(DailyAllowanceItemZ),
		startDate: z.date(),
		startTime: z.iso.time(),
	})
	.strict()
export type DailyAllowanceModel = z.infer<typeof DailyAllowanceModelZ>

export const DailyAllowanceResponseZ = z
	.object({
		...ExpenseResponseZ.shape,
		...DailyAllowanceModelZ.pick({ amount: true, category: true }).shape,
		daily_allowance_rates: z.array(DailyAllowanceItemApiZ),
		date: z.null(),
		destination: z.string(),
		destination_id: z.number(),
		end_date: z.iso.date(),
		end_time: DailyAllowanceModelZ.shape.endTime,
		file_data: DailyAllowanceModelZ.shape.file,
		report_amount: z.number(),
		start_date: z.iso.date(),
		start_time: DailyAllowanceModelZ.shape.startTime,
	})
	.strict()
export type DailyAllowanceResponse = z.infer<typeof DailyAllowanceResponseZ>

export const DailyAllowanceRequestZ = z
	.object({
		...ExpenseRequestZ.shape,
		...DailyAllowanceResponseZ.pick({
			amount: true,
			category: true,
			end_date: true,
			end_time: true,
			expense_status: true,
			expense_type: true,
			file_data: true,
			start_date: true,
			start_time: true,
		}).shape,
		daily_allowance_rates: z.array(
			z.object({
				rate_id: z.number(),
				number_of_days: z.number().positive(),
				amount: z.number(),
			}),
		),
		destination_id: z.number(),
		submitter_id: z.number(),
	})
	.strict()
export type DailyAllowanceRequest = z.infer<typeof DailyAllowanceRequestZ>
